import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useLang, t, tx, getLocale, i18nField } from '../context/LanguageContext';
import translations from '../i18n/translations';
import { ArrowRight, Calendar, ArrowLeft } from 'lucide-react';
import SEO from '../components/SEO';
import { SEO_TEXTES_HORS_PRERENDU } from '../data/seoTextes';
import MarkdownView from '../components/MarkdownView';
import newsSnapshot from '../data/news-snapshot.json';


export default function NewsArticle() {
  const { slug } = useParams();
  const { lang } = useLang();
  const n = translations.news;
  // Initial state depuis le snapshot statique → permet au prerender (react-snap)
  // de capturer l'article complet dans le HTML pré-rendu.
  const initialArticle = newsSnapshot.find((a) => a.slug === slug) || null;
  const [article, setArticle] = useState(initialArticle);
  const [loading, setLoading] = useState(!initialArticle);

  useEffect(() => {
    let cancelled = false;
    fetch(`/api/news/${slug}`)
      .then((res) => (res.ok ? res.json() : Promise.reject()))
      .then((data) => {
        if (!cancelled) setArticle(data);
      })
      .catch(() => {
        // Échec API : on garde le snapshot s'il existait, sinon null.
        if (!cancelled && !initialArticle) setArticle(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [slug, initialArticle]);

  if (loading) {
    return (
      <main className="min-h-screen flex items-center justify-center pt-20">
        <p className="text-slate-400">{t(translations.common.loading, lang)}</p>
      </main>
    );
  }

  if (!article) {
    return (
      <>
        <SEO {...SEO_TEXTES_HORS_PRERENDU['article-introuvable']} path={`/actualites/${slug}`} noindex />
      <main className="min-h-screen flex items-center justify-center pt-20">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-white mb-4">404</h1>
          <p className="text-slate-400 mb-8">{tx(lang, 'Article introuvable', 'Article not found', { es: 'Artículo no encontrado', de: 'Artikel nicht gefunden', it: 'Articolo non trovato', pt: 'Artigo não encontrado' })}</p>
          <Link to="/actualites" className="text-[#0099D6] hover:underline">{t(translations.common.back, lang)}</Link>
        </div>
      </main>
      </>
    );
  }

  const title = i18nField(article, 'title', lang);
  const content = i18nField(article, 'content', lang);
  const excerpt = i18nField(article, 'excerpt', lang);

  return (
    <>
      <SEO
        title={title}
        description={excerpt || `Article METERIS : ${title}.`}
        path={`/actualites/${slug}`}
        image={article.image_url || undefined}
      />
    <main data-testid="news-article-page">
      {/* Hero */}
      <section className="relative pt-32 pb-16">
        {article.image_url && (
          <>
            <div className="absolute inset-0 bg-cover bg-center opacity-15" style={{ backgroundImage: `url(${article.image_url})` }} />
            <div className="absolute inset-0 bg-gradient-to-b from-[#0B0C10] via-[#0B0C10]/95 to-[#0B0C10]" />
          </>
        )}
        <div className="relative max-w-3xl mx-auto px-6 md:px-12">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <Link to="/actualites" className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-[#0099D6] mb-8 transition-colors" data-testid="back-to-news">
              <ArrowLeft className="w-4 h-4" />
              {t(translations.common.back, lang)}
            </Link>
            <div className="flex items-center gap-3 mb-4">
              {article.category && n.categories[article.category] && (
                <span className="px-3 py-1.5 rounded-full bg-[#0099D6]/10 text-xs font-medium text-[#0099D6]">
                  {t(n.categories[article.category], lang)}
                </span>
              )}
              {(article.publish_date || article.created_at) && (
                <span className="flex items-center gap-1 text-xs text-slate-500">
                  <Calendar className="w-3 h-3" />
                  {new Date(article.publish_date || article.created_at).toLocaleDateString(getLocale(lang))}
                </span>
              )}
            </div>
            <h1 className="text-4xl md:text-5xl font-bold tracking-tight text-white mb-6">{title}</h1>
            <p className="text-lg text-slate-300 leading-relaxed">
              <MarkdownView markdown={excerpt} inline />
            </p>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className="pb-24 md:pb-32" data-testid="article-content">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <div className="max-w-none">
            <MarkdownView markdown={content} />
          </div>
        </div>
      </section>

      {/* Article CTA */}
      <section className="py-16 border-t border-white/5">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <div className="bg-[#0099D6]/5 border border-[#0099D6]/20 rounded-2xl p-8 md:p-10 text-center">
            <h3 className="text-2xl font-bold text-white mb-3">{t(n.cta_title, lang)}</h3>
            <p className="text-slate-300 mb-6">{t(n.cta_text, lang)}</p>
            <Link
              to="/contact"
              data-testid="article-cta"
              className="inline-flex items-center gap-2 bg-[#0099D6] hover:bg-[#0284C7] text-white font-semibold py-3 px-6 rounded-lg transition-all hover:scale-105"
            >
              {t(translations.common.audit_cta, lang)}
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>
    </main>
    </>
  );
}
